import { ChangeDetectorRef, Component, ErrorHandler, OnDestroy, OnInit, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { applyEach, FieldTree, form, max, min, required, validate, ValidationError } from '@angular/forms/signals'
import { ActivatedRoute, Router } from '@angular/router'

import { Projekti } from '@lemon/core-firebase/model/kirjanpito-projektit'
import { FirestoreTosite, FirestoreTositteenAlkuperainenTiedosto, Kuitti, Maksutapa, PaymentStatus, SAHKOISET_LASKUT_FAKE_MAKSUTAPA } from '@lemon/core-firebase/model/tosite'

import { KayttajaService } from '../_angular/service/kayttaja.service'
import { TositeService } from '../_angular/service/tosite/tosite.service'

import { arvonMuutokset, asetaArvo, nollaa } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { validoiReaktiivisesti } from '../_jaettu-angular/_lomakkeet/reaktiivinen-validaattori'
import { ProjektitService } from '../_jaettu-angular/projektit.service'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LadataanService } from '../_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { SimplifiedUploadData, TiedostojenLataamisService } from '../_jaettu-angular/service/tiedostojen-lataamis.service'
import { TimestampService } from '../_jaettu-angular/service/timestamp-service'

import { CurrencyService } from '@lemon/core/service/currency.service'
import { DateService } from '@lemon/core/service/date.service'

import { LaskuToimittaja, ToimittajaTunniste, TypeaheadToimittaja } from '@lemon/core-firebase/model/lasku'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { ToimittajaTunnisteService } from '@lemon/core-firebase/service/lasku/toimittaja-tunniste.service'
import { TositeKopioija } from '@lemon/core-firebase/service/tosite/tosite.kopioija'
import { BicService } from '@lemon/core/service/bic.service'
import { IbanService } from '@lemon/core/service/iban.service'
import { StringService } from '@lemon/core/service/string.service'
import { ViitenumeroService } from '@lemon/core/service/viitenumero.service'
import { MaksuLuoUusiComponentData } from 'app/_angular/_resolvers/maksu.resolve'
import { QrTaiViivakoodi } from 'app/_angular/service/barcode/barcode-detector.service'
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject, Subject } from 'rxjs'
import { map, takeUntil, tap } from 'rxjs/operators'
import { FirebaseLemonaid } from '../_angular/service/firebase-lemonaid.service'
import { LaskuService } from '../_angular/service/lasku/lasku.service'
import { TositeDatasourceService } from '../_angular/service/tosite/tosite-datasource.service'
import { FileSystemFileEntry } from '../_jaettu-angular/file-drop/file-drop-dom.types'
import { FileDropEntry } from '../_jaettu-angular/file-drop/file-drop-entry'
import { LaskunToimittajaTypeaheadBase } from '../_jaettu-angular/service/lasku/typeahead.service'
import { TositeKuvaCacheService } from '../tositteet/kuvat/tosite-kuva-cache.service'
interface MaksutLomake {
  tositeArray: MaksuLomake[]
}

interface MaksuLomake {
  pvm: Date | null
  erapvm: Date | null
  summa: number | null
  viite: string
  viesti: string | null
  lisatiedot: string | null
  iban: string
  bic: string
  saaja: string
  toimituspvm: Date | null
  toimittaja: string | TypeaheadToimittaja | null
  ytunnus: string
  projektiAvain: string
}

@Component({
  templateUrl: './maksut-uusi.component.html',
  styleUrls: ['./maksut-uusi.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MaksutUusiComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)
  private readonly _ytunnusPakollinen = signal(true)

  maxDate: Date = new Date(2099, 11, 31)
  minDate: Date = new Date(2023, 0, 1)

  lomake: FieldTree<MaksutLomake>

  uploadTasks: SimplifiedUploadData[] = []
  kuitit: FirestoreTosite[] = []
  readonly virheviestiLatausOnKesken = signal<string>(null)
  readonly virheviestiPysyva = signal<string>(null)
  readonly virheviestiTallennus = signal<string>(null)
  muokataan: boolean = false
  paivitaValidaatiot = {}
  donePercentageObservable: Observable<number>
  latausvirheetObservable: Observable<string[]>
  naytaYhdistaKuitit: boolean = false
  onkoSuoriteTaiLaskuperusteinenObservable: Observable<boolean>
  naytaProjektitObservable: Observable<boolean>
  naytettavatProjektitObservable: Observable<Projekti[]>

  kuvat: string[][] = []
  nakyvatKuvat: string[] = []

  otsikko: string

  // Toimittaja
  readonly toimittaja = signal<LaskuToimittaja>(null)
  alkuperainenToimittaja: LaskuToimittaja = null // Vertailua varten
  readonly muutTunnisteet = signal<string[]>([])
  private _toimittajanTunnisteet: ToimittajaTunniste[] = []
  readonly valittuToimittaja = signal(false)
  readonly naytaToimittajaCheck = signal(false)
  toimittajahaunVihjetekstiObservable: BehaviorSubject<string> = new BehaviorSubject('')
  suodatetutToimittajat: ReplaySubject<TypeaheadToimittaja[]> = new ReplaySubject<TypeaheadToimittaja[]>(1)

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _tositeService: TositeService,
    private _ladataanService: LadataanService,
    private _dateService: DateService,
    private _validationService: FormValidationService,
    private _timestampService: TimestampService,
    private _currencyService: CurrencyService,
    private _kayttajaService: KayttajaService,
    private _tiedostojenLataamisService: TiedostojenLataamisService,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
    private _tositeKuvaCacheService: TositeKuvaCacheService,
    private _tositeDatasourceService: TositeDatasourceService,
    private _tositeKopioija: TositeKopioija,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _ibanService: IbanService,
    private _bicService: BicService,
    private _viitenumeroService: ViitenumeroService,
    private _formValidationService: FormValidationService,
    private _asiakasJaettuService: AsiakasJaettuLemonaidService,
    private _stringService: StringService,
    private _toimittajaTypeahead: LaskunToimittajaTypeaheadBase,
    private _laskuKopioija: LaskuKopioija,
    private _laskuService: LaskuService,
    private _projektitService: ProjektitService,
    private _toimittajaTunnisteService: ToimittajaTunnisteService,
    private _changeDetectorRef: ChangeDetectorRef,
  ) {
    this.naytaProjektitObservable = this._projektitService.naytaProjektitObservable
    this.naytettavatProjektitObservable = this._projektitService.naytettavatKirjanpitoprojektitObservable

    this.maxDate = this._dateService.lisaaKuukausia(new Date(), 12)
    this.minDate = this._dateService.lisaaKuukausia(new Date(), -24)

    this.lomake = form(signal<MaksutLomake>({ tositeArray: [] }), polku => {
      validate(polku.tositeArray, ({ value }) => (value().length < 1 ? { kind: 'min' } : null))
      applyEach(polku.tositeArray, rivi => {
        required(rivi.pvm)
        required(rivi.erapvm)
        required(rivi.summa)
        min(rivi.summa, 0.01)
        max(rivi.summa, 999999.99)
        validate(rivi.viite, ({ value, valueOf }) => this._validoiViestiTaiViite(value(), valueOf(rivi.viesti)))
        validoiReaktiivisesti(rivi.viite, this._validateViitenumero)
        // Signal Formsin maxLength asettaisi tekstikenttään maxlength-attribuutin, joka estäisi kirjoittamasta pidemmälle
        validoiReaktiivisesti(rivi.viesti, Validators.maxLength(60))
        validate(rivi.viesti, ({ value, valueOf }) => this._validoiViestiTaiViite(value(), valueOf(rivi.viite)))
        required(rivi.iban)
        validoiReaktiivisesti(rivi.iban, this._validateIban)
        required(rivi.bic)
        validoiReaktiivisesti(rivi.bic, this._validateBic)
        required(rivi.saaja)
        required(rivi.toimittaja)
        required(rivi.ytunnus, { when: () => this._ytunnusPakollinen() })
      })
    })
  }

  ngOnInit() {
    this._route.data.pipe(takeUntil(this.ngUnsubscribe)).subscribe(
      async (data: { data: MaksuLuoUusiComponentData }) => {
        if (!data || !data.data || !data.data.tiedostot || data.data.tiedostot.length < 1) {
          this.peruuta()
          return
        }

        // this.naytaYhdistaKuitit = data.data.tiedostot.length > 1

        //   // Separate display settings for different tosite types
        //   const lahde = this.tositeService.getMaksutapaNameForRouter(data.data?.maksutapa?.i)
        //   this.setTextsAndFieldsForToTositeType(lahde)
        //   await this.tositeService.setLastViewedKuitinMaksutapa(data.data?.maksutapa?.i)

        const kuitti = this._tositeService.annaUusiKuitti(SAHKOISET_LASKUT_FAKE_MAKSUTAPA)
        kuitti.currency = 'EUR'
        kuitti.type = 'payment'
        kuitti.p = null
        kuitti.localPvm = null
        kuitti.pvm = null
        const rivi = this._lisaaMaksunRivi(kuitti)
        this.kuitit.push(kuitti)
        kuitti.maksutiedot = this._tositeKopioija.annaMaksutiedot(kuitti)

        // Tämä implementaatio tekee yhden maksun, jossa kaikki kuvat
        await this._alusta(kuitti, rivi, data.data.tiedostot)

        // const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
        //   for (const tiedosto of data.data.tiedostot) {

        //     const kuitti = this.tositeService.annaUusiKuitti(data.data.maksutapa)
        //     if (data.data.maksutapa) {
        //       kuitti.maksutapa = data.data.maksutapa.i
        //     }
        //     const tositeGroup = this.annaKuitinFormGroup(kuitti)

        //     this.kuitit.push(kuitti)
        //     this.tositeArray.push(tositeGroup)

        //     const fileEnding = this.tiedostojenLataamisService.getFileEndingFromNgxFileDropEntry(tiedosto)
        //     const file = await this.tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)

        //     const alkuperainen: FirestoreTositteenAlkuperainenTiedosto = {
        //       avain: this._firebaseLemonaid.firestoreCreateId(),
        //       kuvakansio: kuitti.kuvakansio,
        //       nimi: tiedosto.relativePath,
        //       fileEnding: fileEnding,
        //       kasitelty: false
        //     }
        //     kuitti.alkuperaiset[alkuperainen.avain] = alkuperainen

        //     const uploadData = this.tiedostojenLataamisService.tallennaTositetiedosto(this._firebaseLemonaid, kayttajanTiedot.asiakasId + '', alkuperainen, file)
        //     this.uploadTasks.push(uploadData)

        //     const purettuTiedosto = await this.tositeService.puraYksiTiedosto(kuitti, file, tiedosto, fileEnding, uploadData, alkuperainen, kayttajanTiedot)
        //     if (purettuTiedosto.viivakoodi) {
        //       tositeGroup.get('summa').setValue(this.currencyService.muutaBigDecimalRahaksi(purettuTiedosto.viivakoodi.summa))
        //     }
        //     if (purettuTiedosto.virhe) {
        //       this.virheviestiPysyva = this.virheviestiPysyva ? this.virheviestiPysyva + ' ' + purettuTiedosto.virhe : purettuTiedosto.virhe
        //     }
        //     if (purettuTiedosto.kasitellytKuvat) {
        //       for (const kuva of purettuTiedosto.kasitellytKuvat) {
        //         kuitti.kuvat[kuva.avain] = kuva
        //       }
        //     }

        //     const tamanKuitinKuvat = this.tositeKuvaCacheService.annaKuvienUrlit(kuitti, kayttajanTiedot, 'full')
        //     this.kuvat.push(tamanKuitinKuvat)
        //     this.nakyvatKuvat.push(tamanKuitinKuvat.length > 0 ? tamanKuitinKuvat[0] : 'asdfdsfdsfdfsasdffss')
        //   }

        //   this.donePercentageObservable = this.tiedostojenLataamisService.annaKokonaisprosentti(this.uploadTasks).pipe(
        //     tap(percentage => {
        //       if (percentage > 99.99) {
        //         this.virheviestiLatausOnKesken = null
        //       }
        //     })
        //   )
        //   this.latausvirheetObservable = this.tiedostojenLataamisService.annaKaikkiVirheet(this.uploadTasks)

        //   this.asetaOletusprosentit(this.oletusAlv, this.tositeArray, this.kuitit)
      },
      error => {
        this._errorHandler.handleError(error)
      },
    )

    this.onkoSuoriteTaiLaskuperusteinenObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(
      map(asiakas => {
        if (!asiakas?.kirjanpidonPerusteet?.length) {
          return false
        }
        const currentLocalDate = this._dateService.currentLocalDate()
        return this._asiakasJaettuService.onkoKirjanpidonPerusteSuoriteTaiLaskuperusteinen(asiakas, currentLocalDate)
      }),
    )

    // const apixConfigObservable = this._kayttajaService.kayttajaObservable.pipe(
    //   switchMap(kayttaja => {
    //     if (!kayttaja) {
    //       return of<ApixReceivedInvoiceConfig>(null)
    //     }
    //     return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + kayttaja.asiakasAvain + '/apix-received-invoice-config/' + kayttaja.asiakasAvain).listen()
    //   })
    // )
  }

  private async _alusta(kuitti: FirestoreTosite, rivi: FieldTree<MaksuLomake>, tiedostot: FileDropEntry[]) {
    const kayttajanTiedot = await this._kayttajaService.getKayttajanTiedot()
    let koodiPromise: Promise<QrTaiViivakoodi> = Promise.resolve({})
    for (const tiedosto of tiedostot) {
      const fileEnding = this._tiedostojenLataamisService.getFileEndingFromNgxFileDropEntry(tiedosto)
      const file = await this._tiedostojenLataamisService.getFile(tiedosto.fileEntry as FileSystemFileEntry)

      const alkuperainen: FirestoreTositteenAlkuperainenTiedosto = {
        avain: this._firebaseLemonaid.firestoreCreateId(),
        kuvakansio: kuitti.kuvakansio,
        nimi: tiedosto.relativePath,
        fileEnding: fileEnding,
        kasitelty: false,
      }
      kuitti.alkuperaiset[alkuperainen.avain] = alkuperainen

      const uploadData = this._tiedostojenLataamisService.tallennaTositetiedostoSimplified(this._firebaseLemonaid, kayttajanTiedot.asiakasId + '', alkuperainen, file)
      this.uploadTasks.push(uploadData)

      const purettuTiedosto = await this._tositeService.puraYksiTiedostoLokaalisti(kuitti, file, tiedosto, fileEnding, uploadData, alkuperainen, kayttajanTiedot, true, koodiPromise)
      koodiPromise = purettuTiedosto.koodi
      if (purettuTiedosto.virhe) {
        if (purettuTiedosto.virhe === 'purkuvirhe') {
          // Aloita back-end käsittely kaikille tositteille, jotka tarvitsevat sitä
          // Älä blokkaa!
          this._tositeService.puraYksiTiedostoBackendissa(kuitti, uploadData, alkuperainen, kayttajanTiedot, true, Promise.resolve({})).then(purettuTiedosto => {
            if (purettuTiedosto.virhe) {
              const virheviesti = this._annaVirhe(purettuTiedosto.virhe, uploadData.filename)
              this.virheviestiPysyva.set(this.virheviestiPysyva() ? this.virheviestiPysyva() + ' ' + virheviesti : virheviesti)
            } else {
              if (purettuTiedosto.kasitellytKuvat) {
                for (const kuva of purettuTiedosto.kasitellytKuvat) {
                  kuitti.kuvat[kuva.avain] = kuva
                }
              }
              const tamanKuitinKuvat = this._tositeKuvaCacheService.annaKuvienUrlit(kuitti, kayttajanTiedot, 'full')
              this.kuvat.push(tamanKuitinKuvat)
              this.nakyvatKuvat.push(tamanKuitinKuvat.length > 0 ? tamanKuitinKuvat[0] : 'asdfdsfdsfdfsasdffss')
              this._changeDetectorRef.markForCheck()
            }
          })
        } else {
          const virheviesti = this._annaVirhe(purettuTiedosto.virhe, uploadData.filename)
          this.virheviestiPysyva.set(this.virheviestiPysyva() ? this.virheviestiPysyva() + ' ' + virheviesti : virheviesti)
        }
      } else if (purettuTiedosto.kasitellytKuvat) {
        for (const kuva of purettuTiedosto.kasitellytKuvat) {
          kuitti.kuvat[kuva.avain] = kuva
        }
        const tamanKuitinKuvat = this._tositeKuvaCacheService.annaKuvienUrlit(kuitti, kayttajanTiedot, 'full')
        this.kuvat.push(tamanKuitinKuvat)
        this.nakyvatKuvat.push(tamanKuitinKuvat.length > 0 ? tamanKuitinKuvat[0] : 'asdfdsfdsfdfsasdffss')
        this._changeDetectorRef.markForCheck()
      }
    }

    koodiPromise.then(koodi => {
      // console.log('KOODIT TÄSSÄ', koodi)
      if (koodi.qr) {
        asetaArvo(rivi.summa, this._currencyService.roundHalfUp(koodi.qr.summa, 2))
        asetaArvo(rivi.iban, koodi.qr.iban ?? '')
        asetaArvo(rivi.erapvm, this._dateService.localDateToDate(koodi.qr.erapaiva))
        asetaArvo(rivi.viite, this._viitenumeroService.muotoileViitenumero(koodi.qr.viitenumero) ?? '')
        asetaArvo(rivi.saaja, koodi.qr.maksunSaajaNimi ?? '')
      } else if (koodi.viiva) {
        asetaArvo(rivi.summa, this._currencyService.muutaBigDecimalRahaksi(koodi.viiva.summa))
        asetaArvo(rivi.iban, koodi.viiva.iban ?? '')
        asetaArvo(rivi.erapvm, this._dateService.localDateToDate(koodi.viiva.erapvm))
        asetaArvo(rivi.viite, this._viitenumeroService.muotoileViitenumero(koodi.viiva.viitenumero) ?? '')
        // kuitinFormGroup.get('saaja').setValue(koodi.qr.maksunSaajaNimi)
      }
    })

    this.donePercentageObservable = this._tiedostojenLataamisService.annaKokonaisprosenttiSimplified(this.uploadTasks).pipe(
      tap(percentage => {
        if (percentage > 99.99) {
          this.virheviestiLatausOnKesken.set(null)
        }
      }),
    )
    this.latausvirheetObservable = this._tiedostojenLataamisService.annaKaikkiVirheetSimplified(this.uploadTasks)
    this._changeDetectorRef.markForCheck()
  }

  private _annaVirhe(virhe: string, tiedostonNimi: string): string {
    if (virhe === 'purkuvirhe') {
      return this._translationService.lokalisoi('kuitit.muokkaa.validation.purkuvirhe-myos-backend', { file_name: tiedostonNimi })
    }
    if (virhe === 'latausvirhe') {
      return this._translationService.lokalisoi('kuitit.muokkaa.validation.latausvirhe', { file_name: tiedostonNimi })
    }
    return this._translationService.lokalisoi('kuitit.muokkaa.validation.tuntematon-virhe', { file_name: tiedostonNimi })
  }

  get maksuLomake(): FieldTree<MaksuLomake> {
    return this.lomake.tositeArray[0] // Näytöllä vain yksi kuitti, kovakoodataan
  }

  get nykyinenKuitti(): FirestoreTosite {
    return this.kuitit[0] // Näytöllä vain yksi kuitti, kovakoodataan
  }

  private _lisaaMaksunRivi(kuitti: FirestoreTosite): FieldTree<MaksuLomake> {
    const paymentData = this._tositeKopioija.annaMaksutiedot(kuitti)

    const rivit = this.lomake.tositeArray().value()
    const uusiRivi: MaksuLomake = {
      pvm: this._dateService.localDateToDate(kuitti.localPvm) ?? null,
      erapvm: this._dateService.numberToDate(paymentData.dueDate) ?? null,
      summa: paymentData.amount ?? null,
      viite: paymentData.reference ?? '',
      viesti: paymentData.message ?? null,
      lisatiedot: kuitti.selite ?? null,
      iban: paymentData.iban ?? '',
      bic: paymentData.bic ?? '',
      saaja: paymentData.maksunSaajanNimi ?? '',
      toimituspvm: null,
      toimittaja: null,
      ytunnus: '',
      projektiAvain: kuitti.projektiAvain || '',
    }
    asetaArvo(this.lomake.tositeArray, [...rivit, uusiRivi], { ilmoita: false })
    const rivi = this.lomake.tositeArray[rivit.length]
    const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe))

    muutokset(rivi.projektiAvain).subscribe(arvo => {
      kuitti.projektiAvain = arvo || null
    })

    muutokset(rivi.pvm).subscribe((value: Date) => {
      kuitti.pvm = this._timestampService.dateToTimestamp(value)
      kuitti.localPvm = this._dateService.dateToLocalDate(value)
      const numberDate = this._dateService.dateToNumber(value)
      kuitti.p = numberDate
      kuitti.invoiceDate = numberDate
    })
    muutokset(rivi.erapvm).subscribe((value: Date) => {
      kuitti.dueDate = this._dateService.dateToNumber(value)
      kuitti.maksutiedot.dueDate = kuitti.dueDate
    })
    muutokset(rivi.summa).subscribe(value => {
      kuitti.summa = value
      kuitti.sum = value
      kuitti.maksutiedot.amount = value
    })
    muutokset(rivi.lisatiedot).subscribe(value => {
      kuitti.selite = value
    })
    muutokset(rivi.viite).subscribe(value => {
      kuitti.viitenumero = this._stringService.removeAllWhiteSpaces(value)
      kuitti.maksutiedot.reference = this._stringService.removeAllWhiteSpaces(value)
    })
    muutokset(rivi.viesti).subscribe(value => {
      kuitti.maksutiedot.message = value
    })
    muutokset(rivi.iban).subscribe(value => {
      const iban = this._stringService.removeAllWhiteSpaces(value)
      kuitti.iban = iban
      kuitti.maksutiedot.iban = iban
      if (this._ibanService.isValidIban(kuitti.iban) && !kuitti.bic?.trim()) {
        asetaArvo(rivi.bic, this._bicService.annaBicKoodi(kuitti.iban) ?? '')
      }
    })
    muutokset(rivi.bic).subscribe(value => {
      const bic = this._stringService.removeAllWhiteSpaces(value)
      kuitti.bic = bic
      kuitti.maksutiedot.bic = bic
    })
    muutokset(rivi.saaja).subscribe(value => this._saajaMuuttui(kuitti, value))
    muutokset(rivi.toimituspvm).subscribe((value: Date) => {
      kuitti.deliveryDate = this._dateService.dateToNumber(value)
    })
    muutokset(rivi.toimittaja).subscribe((value: string | TypeaheadToimittaja) => {
      if (this._stringService.isString(value)) {
        if (this.toimittaja()) {
          this.toimittaja().nimi = value
        } else {
          this.asetaUusiToimittaja()
        }
        this.asetaToimittajahaunVihjeteksti()
        if (!this.valittuToimittaja()) {
          const kasitelty = this._validationService.processValue(value)
          this._toimittajaTypeahead
            .haeJaSuodata(kasitelty, false)
            .then(result => {
              this.suodatetutToimittajat.next(result.suodatetut)
            })
            .catch(err => {
              this._errorHandler.handleError(err)
            })
        }
      } else if (value) {
        if (this.valittuToimittaja()) {
          this.asetaToimittajahaunVihjeteksti()
          if (this.toimittaja()) {
            this.toimittaja().nimi = value.nimi
          } else {
            this.asetaUusiToimittaja()
          }
        } else {
          this._asetaTypeaheadToimittaja(value)
        }
      } else {
        this.tyhjennaToimittajahaunVihjeteksti()
      }
    })
    muutokset(rivi.ytunnus).subscribe(value => this._ytunnusMuuttui(kuitti, value))

    return rivi
  }

  private _saajaMuuttui(kuitti: FirestoreTosite, value: string | null) {
    kuitti.seller = value
    kuitti.maksutiedot.maksunSaajanNimi = value

    if (this.toimittaja() && this.toimittaja().maksunSaaja !== value) {
      this.toimittaja().maksunSaaja = value
      this.asetaToimittajahaunVihjeteksti()
    }
  }

  private _ytunnusMuuttui(kuitti: FirestoreTosite, value: string | null) {
    kuitti.sellerId = value
    if (this.toimittaja() && this.toimittaja().ytunnus !== value) {
      this.toimittaja().ytunnus = value
      this.asetaToimittajahaunVihjeteksti()
    }
  }

  private _validoiViestiTaiViite(arvo: string | null, toinen: string | null): ValidationError | null {
    if ((arvo == null || arvo === undefined || arvo.trim() === '') && (toinen == null || toinen === undefined || toinen.trim() === '')) {
      return { kind: 'viestitaiviite' }
    }
    return null
  }

  private _validateIban: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._ibanService.isValidIban(ctrl.value)) {
      return { invalidiban: true }
    }
    return null
  }

  private _validateBic: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._bicService.validoiBic(ctrl.value)) {
      return { invalidbic: true }
    }
    return null
  }

  private _validateViitenumero: ValidatorFn = (ctrl: AbstractControl): ValidationErrors | null => {
    if (ctrl.value == null || ctrl.value === undefined || ctrl.value.trim() === '') {
      return null
    }
    if (!this._viitenumeroService.onkoViitenumeroValidi(ctrl.value)) {
      return { viitenumero: true }
    }
    return null
  }

  vaihdaNakyvaKuva(index: number, url: string) {
    this.nakyvatKuvat[index] = url
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next()
    this.ngUnsubscribe.complete()
  }

  public valitseMaksutapa(maksutapa: Maksutapa, kuitti: Kuitti) {
    kuitti.p = maksutapa.i
  }

  private _saveInFlight = false
  async save() {
    if (await this._kayttajaService.kayttajaaImpersonoidaan()) {
      return
    }

    if (this._saveInFlight) {
      return
    }
    this._saveInFlight = true
    this.virheviestiTallennus.set(null)

    try {
      this._ladataanService.aloitaLataaminen()

      const onkoTallentaminenEstetty = await this._tositeService.saveIsPreventedSimplified(this.lomake, this.latausvirheetObservable, this.uploadTasks, this.virheviestiPysyva())
      if (onkoTallentaminenEstetty && onkoTallentaminenEstetty.tallennusEstetty) {
        if (onkoTallentaminenEstetty.lataaminenKesken) {
          this.virheviestiLatausOnKesken.set(this._translationService.lokalisoi('kuitit.muokkaa.validation.ladataan'))
        }
        this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
        setTimeout(() => {
          this._formValidationService.naytaEnsimmainenVirhe()
        }, 50)
        return
      }

      // Kopioidaan tallennusyrityksen ajaksi, jotta ei muokata alkuperäistä toimittajaa ennen kuin tallennus on onnistunut
      const copiedToimittaja = this.toimittaja() ? this._laskuKopioija.copyToimittaja(this.toimittaja()) : null

      if (copiedToimittaja) {
        if (!this._currencyService.onkoNumero(copiedToimittaja.numero)) {
          // Numero puuttuu, haetaan seuraava numero
          const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
          const numero = await this._laskuService.annaSeuraavaToimittajaNumero(kayttajanTiedot.asiakasAvain)
          copiedToimittaja.numero = numero
        }

        if (!copiedToimittaja.avain) {
          copiedToimittaja.avain = this._firebaseLemonaid.firestoreCreateId()
          copiedToimittaja.luotu = this._timestampService.now()
          const kayttajanTiedot = await firstValueFrom(this._kayttajaService.kayttajanTiedotObservable)
          copiedToimittaja.luoja = kayttajanTiedot.uid
          // Luodaan oletus IBAN tiedot uudelle toimittajalle
          const iban = this._stringService.removeAllWhiteSpaces(this.maksuLomake.iban().value())
          const bic = this._stringService.removeAllWhiteSpaces(this.maksuLomake.bic().value())
          copiedToimittaja.iban = {
            [iban]: {
              iban: iban,
              bic: bic,
              tyyppi: 'oletus',
            },
          }
        }

        // Validoidaan toimittajan tiedot, jos se ollaan luomassa / päivitetään olemassaolevaa
        const toimittajaErrors = await this._laskuService.validoiToimittaja(this.alkuperainenToimittaja, copiedToimittaja)

        if (Object.values(toimittajaErrors).some(v => v)) {
          this._validationService.merkitseKokoLomakeKosketuksi(this.lomake)
          this._ladataanService.lopetaLataaminen()
          return
        }
      }

      if (!this.lomake().valid()) {
        this._formValidationService.merkitseKokoLomakeKosketuksi(this.lomake)
        setTimeout(() => {
          this._formValidationService.naytaEnsimmainenVirhe()
        }, 50)
        this._saveInFlight = false
        return
      }

      for (const kuitti of this.kuitit) {
        const eraantynyt = this._dateService.compareNumberDates(kuitti.dueDate, '<', this._dateService.currentNumberDate())
        kuitti.paymentStatus = eraantynyt ? PaymentStatus.ERAANTYNYT : PaymentStatus.AVOIN
        if (copiedToimittaja) {
          kuitti.toimittajaAvain = copiedToimittaja.avain
        }
      }

      // Tallennetaan ensin toimittaja (voi olla uuden luonti tai päivitys)
      if (copiedToimittaja) {
        const kentanTunniste = this._validationService.processValue(this.maksuLomake.ytunnus().value()) || null
        const tunnisteetData = this._toimittajaTunnisteService.vaihdaMuokattavaTunniste(this._toimittajanTunnisteet, kentanTunniste)
        const tunnistekentat = this._toimittajaTunnisteService.muodostaTunnistekentatDatasta(tunnisteetData)
        copiedToimittaja.ytunnus = tunnistekentat.ytunnus
        copiedToimittaja.tunnisteetData = tunnistekentat.tunnisteetData
        copiedToimittaja.tunnisteet = tunnistekentat.tunnisteet
        await this._tositeService.tallennaToimittaja(copiedToimittaja)
      }
      await this._tositeService.tallennaKuitit(this.kuitit)
      this._router.navigate(['maksut'])
    } catch (error) {
      this.virheviestiTallennus.set(this._translationService.lokalisoi('tositteet.yhteiset.tallentaminen-epaonnistui'))
      this._errorHandler.handleError(error)
    } finally {
      this._saveInFlight = false
      this._ladataanService.lopetaLataaminen()
    }
  }

  peruuta() {
    this._router.navigate(['maksut'])
  }

  // Toimittaja
  poistaValittuToimittaja() {
    this._toimittajaMuuttui(null)
    nollaa(this.maksuLomake.toimittaja, null)
  }

  tarkistaToimittajaPoisto() {
    if (!this.maksuLomake.toimittaja().value()) {
      this.poistaValittuToimittaja()
    }
  }

  onToimittajaPaste(event: ClipboardEvent) {
    setTimeout(() => {
      this._ilmoitaArvoUudelleen(this.maksuLomake.toimittaja)
    }, 0) // Timeout needed to move the validation to the end of the call stack, i.e. to ensure that new FormControl value is set already when the validation restarts.
  }

  private asetaUusiToimittaja() {
    const toimittaja = this._laskuKopioija.annaUusiToimittaja()
    this.toimittaja.set(toimittaja)
    this._asetaToimittajanTunnisteet([])

    // Päivitetään myös toimittajaan liittyvät kentät, jotta saadaan uudet arvot päivittymään
    this._ilmoitaArvoUudelleen(this.maksuLomake.iban)
    this._ilmoitaArvoUudelleen(this.maksuLomake.bic)
    this._ilmoitaArvoUudelleen(this.maksuLomake.saaja)
    this._ilmoitaArvoUudelleen(this.maksuLomake.ytunnus)
  }

  // Vastaa reaktiivisen lomakkeen updateValueAndValidity-kutsua, joka lähetti kentän nykyisen arvon tilaajille uudelleen
  private _ilmoitaArvoUudelleen<T>(kentta: FieldTree<T>) {
    asetaArvo(kentta, kentta().value())
  }

  private tyhjennaToimittajahaunVihjeteksti() {
    this.toimittajahaunVihjetekstiObservable.next('')
  }

  private asetaToimittajahaunVihjeteksti() {
    if (this.valittuToimittaja()) {
      this.toimittajahaunVihjetekstiObservable.next(this._translationService.lokalisoi('maksut.muokkaa.muokkaa-toimittajaa'))
    } else {
      this.toimittajahaunVihjetekstiObservable.next(this._translationService.lokalisoi('maksut.muokkaa.tallenna-uutena-toimittajana'))
    }
  }

  naytettavaTunniste(ytunnus: string | null) {
    return this._toimittajaTunnisteService.naytettavaTunnisteArvosta(ytunnus)
  }

  toimittajaDisplayFn(toimittaja: LaskuToimittaja): string {
    if (toimittaja && toimittaja.nimi) {
      return toimittaja.nimi
    }
    return ''
  }

  private _asetaTypeaheadToimittaja(typeaheadToimittaja: TypeaheadToimittaja) {
    if (!typeaheadToimittaja || !typeaheadToimittaja.avain) {
      return
    }

    this._toimittajaTypeahead
      .annaToimittaja(typeaheadToimittaja.avain)
      .then(toimittaja => {
        if (toimittaja) {
          this.tyhjennaToimittajahaunVihjeteksti()
          this._toimittajaMuuttui(toimittaja)
          this.suodatetutToimittajat.next([])
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
      })
  }

  private _toimittajaMuuttui(toimittaja: LaskuToimittaja) {
    if (toimittaja) {
      this.toimittaja.set(toimittaja)
      this.alkuperainenToimittaja = this._laskuKopioija.copyToimittaja(toimittaja)
      this.nykyinenKuitti.toimittajaAvain = toimittaja.avain
      this.valittuToimittaja.set(true)
      this.naytaToimittajaCheck.set(true)
      this.toimittajahaunVihjetekstiObservable.next(this._translationService.lokalisoi('maksut.muokkaa.haettu-rekisterista'))

      // Asetetaan arvot toimittajasta
      this._asetaToimittajanTunnisteet(this._toimittajaTunnisteService.nykyisetTunnisteet(toimittaja))
      // Tekstikentän arvo on aina merkkijono, mutta käsittelijät saavat puuttuvan tunnisteen ja maksun saajan kuten ennenkin,
      // jotta toimittajan tiedot ja hakukentän vihjeteksti pysyvät ennallaan
      const ytunnus = this._toimittajaTunnisteService.annaMuokattavaTunniste(this._toimittajanTunnisteet)
      asetaArvo(this.maksuLomake.ytunnus, ytunnus ?? '', { ilmoita: false })
      this._ytunnusMuuttui(this.nykyinenKuitti, ytunnus)
      asetaArvo(this.maksuLomake.saaja, toimittaja.maksunSaaja ?? '', { ilmoita: false })
      this._saajaMuuttui(this.nykyinenKuitti, toimittaja.maksunSaaja)

      const oletusIban = toimittaja.iban ? Object.values(toimittaja.iban).find(i => i.tyyppi === 'oletus') : null
      const iban = oletusIban ? oletusIban.iban : null
      const bic = oletusIban ? oletusIban.bic : null
      asetaArvo(this.maksuLomake.iban, iban ?? '')
      asetaArvo(this.maksuLomake.bic, bic ?? '')
    } else {
      this.toimittaja.set(null)
      this.nykyinenKuitti.toimittajaAvain = null
      this.valittuToimittaja.set(false)
      this.naytaToimittajaCheck.set(false)
      this._asetaToimittajanTunnisteet([])
      this.asetaToimittajahaunVihjeteksti()
    }
  }

  private _asetaToimittajanTunnisteet(tunnisteet: ToimittajaTunniste[]) {
    this._toimittajanTunnisteet = tunnisteet
    this.muutTunnisteet.set(this._toimittajaTunnisteService.annaMuutTunnisteet(tunnisteet))
    this._ytunnusPakollinen.set(!this.muutTunnisteet().length)
  }
}
